// Mescla a lista NATIVA de white labels do portal (fonte do seletor
// de lojas do header — useWhiteLabels) com os WLs que JÁ têm
// configuração no notification-app (GET /notification-center/
// white-labels). A lista de Configurações precisa exibir TODOS os
// WLs do portal, não só os já configurados:
// - "configured"  → tem configuração (card com link pro detalhe);
// - "unconfigured" → só existe no portal (card "não configurado"
//   com CTA de criação da configuração padrão).
//
// Identidade: o `id` do WL configurado (WHL-…) corresponde ao `uuid`
// da loja do portal. WLs configurados sem loja correspondente ainda
// aparecem (união), para nunca esconder uma configuração existente.
import type { NotificationCenterWhiteLabelsResponseType } from "@/types/notification-center"

// Loja do portal como o seletor do header consome (subset do
// WhitelabelType — só o que a mescla precisa).
export type PortalStore = {
  uuid: string
  name: string
}

export type MergedWhiteLabelItem =
  | {
      status: "configured"
      id: string
      name: string
      whiteLabel: NotificationCenterWhiteLabelsResponseType[number]
    }
  | { status: "unconfigured"; id: string; name: string }

export const mergeWhiteLabels = (
  portalStores: PortalStore[],
  configured: NotificationCenterWhiteLabelsResponseType
): MergedWhiteLabelItem[] => {
  const configuredById = new Map(
    configured.map((whiteLabel) => [
      whiteLabel.id,
      whiteLabel,
    ])
  )
  const matchedIds = new Set<string>()
  const merged: MergedWhiteLabelItem[] = []

  // "Todas as lojas" (uuid vazio) é o agregado do seletor, não um WL.
  for (const store of portalStores) {
    if (!store.uuid) continue
    const configuredWhiteLabel = configuredById.get(
      store.uuid
    )
    if (configuredWhiteLabel) {
      matchedIds.add(configuredWhiteLabel.id)
      merged.push({
        status: "configured",
        id: configuredWhiteLabel.id,
        // Prioriza o nome HUMANO da loja (fonte do seletor do header). O
        // notification-app não guarda o nome do WL — devolve o próprio uuid
        // em `name` —, então o backend só serve de fallback (WL sem loja).
        name: store.name || configuredWhiteLabel.name,
        whiteLabel: configuredWhiteLabel,
      })
    } else {
      merged.push({
        status: "unconfigured",
        id: store.uuid,
        name: store.name,
      })
    }
  }

  // WLs configurados sem loja correspondente no portal (união).
  for (const whiteLabel of configured) {
    if (matchedIds.has(whiteLabel.id)) continue
    merged.push({
      status: "configured",
      id: whiteLabel.id,
      name: whiteLabel.name,
      whiteLabel,
    })
  }

  return merged
}
